import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import ClientCard from '../../components/dashboard/ClientCard';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [practitioner, setPractitioner] = useState(null);

  useEffect(() => {
    if (user) {
      loadDashboard();
    }
  }, [user]);

  async function loadDashboard() {
    setLoading(true);

    // Get practitioner record
    const { data: prac } = await supabase
      .from('practitioners')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    setPractitioner(prac);

    if (!prac) {
      setLoading(false);
      return;
    }

    // Get all clients with their protocol items and today's data
    const today = new Date().toISOString().split('T')[0];

    const { data: clientList, error } = await supabase
      .from('clients')
      .select(`
        *,
        protocol_items ( id, is_active ),
        checkoffs ( id, date ),
        daily_logs ( id, date )
      `)
      .eq('practitioner_id', prac.id)
      .eq('protocol_items.is_active', true)
      .order('created_at', { ascending: false });

    if (!error && clientList) {
      // Compute stats for each client
      const enriched = clientList.map((client) => {
        const activeItems = (client.protocol_items || []).filter(i => i.is_active !== false);
        const totalItems = activeItems.length;

        // Days since start
        const startDate = new Date(client.start_date || client.created_at);
        const now = new Date();
        const daysSinceStart = Math.max(1, Math.floor((now - startDate) / (1000 * 60 * 60 * 24)));

        // Tracking rate: total checkoffs / (active items × days since start)
        const totalCheckoffs = (client.checkoffs || []).length;
        const expectedCheckoffs = totalItems * daysSinceStart;
        const trackingRate = expectedCheckoffs > 0
          ? Math.min(100, Math.round((totalCheckoffs / expectedCheckoffs) * 100))
          : 0;

        // Logged today?
        const loggedToday = (client.daily_logs || []).some(log => log.date === today);

        return {
          ...client,
          totalItems,
          daysSinceStart,
          trackingRate,
          loggedToday,
        };
      });

      setClients(enriched);
    }

    setLoading(false);
  }

  async function removeClient(clientId) {
    // Delete related records first (order matters for foreign keys)
    await supabase.from('checkoffs').delete().eq('client_id', clientId);
    await supabase.from('custom_answers').delete().eq('client_id', clientId);
    await supabase.from('daily_logs').delete().eq('client_id', clientId);
    await supabase.from('photos').delete().eq('client_id', clientId);
    await supabase.from('messages').delete().eq('client_id', clientId);
    await supabase.from('protocol_events').delete().eq('client_id', clientId);
    await supabase.from('protocol_items').delete().eq('client_id', clientId);
    await supabase.from('custom_questions').delete().eq('client_id', clientId);

    // Delete the client record
    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', clientId);

    if (error) {
      console.error('Failed to remove client:', error);
      return;
    }

    // Remove from local state
    setClients((prev) => prev.filter((c) => c.id !== clientId));
  }

  const filtered = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="screen container">
        <div className="dash-loading">
          <p>Loading your clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="screen container">
      <header className="dash-header">
        <div>
          <h1 className="dash-title">Clients</h1>
          {practitioner?.practice_name && (
            <p className="dash-subtitle">{practitioner.practice_name}</p>
          )}
        </div>
        <Link to="/practitioner/clients/add" className="dash-add-btn">
          + Add Client
        </Link>
      </header>

      {clients.length > 0 && (
        <div className="dash-search-wrap">
          <svg className="dash-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="dash-search"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {filtered.length === 0 && clients.length === 0 ? (
        <div className="dash-empty">
          <div className="dash-empty-icon">🌿</div>
          <h3>No clients yet</h3>
          <p>Add your first client to get started with Correly.</p>
          <Link to="/practitioner/clients/add" className="btn btn-primary" style={{ marginTop: '16px' }}>
            + Add Your First Client
          </Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="dash-empty">
          <p>No clients match "{search}"</p>
        </div>
      ) : (
        <div className="dash-client-list">
          {filtered.map((client) => (
            <ClientCard key={client.id} client={client} onRemove={removeClient} />
          ))}
        </div>
      )}

      <div className="dash-stats-bar">
        <span>{clients.length} client{clients.length !== 1 ? 's' : ''}</span>
        <span>·</span>
        <span>{clients.filter(c => c.loggedToday).length} logged today</span>
      </div>
    </div>
  );
}
